import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from 'sonner';
import Login from "@/components/Login";
import Dashboard from "@/components/Dashboard";
import { startLocationMonitoring, stopLocationMonitoring } from "@/utils/gpsRestriction";
import DesignHub from "@/components/DesignHub";
import TaskManagement from "@/components/TaskManagement";
import MasterData from "@/components/modules/MasterDataNew";
import VendorData from "@/components/modules/master-data/VendorData";
import FabricData from "@/components/modules/master-data/FabricData";
import TrimsData from "@/components/modules/master-data/TrimsData";
import PackagingData from "@/components/modules/master-data/PackagingData";
import ArticleData from "@/components/modules/master-data/ArticleData";
import DigitalDesignData from "@/components/modules/master-data/DigitalDesignData";
import Inventory from "@/components/modules/Inventory";
import IncomingOrders from "@/components/modules/IncomingOrders";
import PurchaseOrders from "@/components/modules/PurchaseOrders";
import SamplingDashboard from "@/components/modules/SamplingDashboard";
import SampleTracker from "@/components/modules/SampleTracker";
import WorkOrders from "@/components/modules/WorkOrders";
import TimeTracker from "@/components/modules/TimeTracker";
import Techpack from "@/components/modules/Techpack";
import Costing from "@/components/modules/Costing";
import ProductionArticles from "@/components/modules/ProductionArticles";
import DigitalDesign from "@/components/modules/DigitalDesign";
import ExternalWorkOrders from "@/components/modules/ExternalWorkOrders";
import ProductionTracker from "@/components/modules/ProductionTracker";
import Expenses from "@/components/modules/ExpensesNew";
import ProductionOrders from "@/components/modules/ProductionOrders";
import ProductionPurchaseOrders from "@/components/modules/ProductionPurchaseOrders";
import ProductionRawInventory from "@/components/modules/ProductionRawInventory";
import ProductionFinishedInventory from "@/components/modules/ProductionFinishedInventory";
import ProductionPlanning from "@/components/modules/ProductionPlanning";
import QualityControl from "@/components/modules/QualityControl";
import CuttingRoom from "@/components/modules/CuttingRoom";
import SewingFloor from "@/components/modules/SewingFloor";
import FinishingPacking from "@/components/modules/FinishingPacking";
import ProductionReports from "@/components/modules/ProductionReports";
import DeliveryManagement from "@/components/modules/DeliveryManagement";
import ProductionCosting from "@/components/modules/ProductionCosting";
import UserManagement from "@/components/UserManagement";
import UserAccessControl from "@/components/UserAccessControl";
import EmployeeActionsLedger from "@/components/EmployeeActionsLedger";
import FashionDesign from "@/components/modules/FashionDesign";
import SamplingTimeTracker from "@/components/modules/SamplingTimeTracker";
import Wastage from "@/components/modules/Wastage";
import GatePass from "@/components/modules/GatePass";
import DesignSampling from "@/components/modules/task-management/DesignSampling";
import Merchandise from "@/components/modules/task-management/Merchandise";
import DigitalDesignTask from "@/components/modules/task-management/DigitalDesign";
import MeetingMinutes from "@/components/modules/task-management/MeetingMinutes";
import WeeklyProgress from "@/components/modules/task-management/WeeklyProgress";
import ExecutiveBriefing from "@/components/modules/task-management/ExecutiveBriefing";
import ExecutiveBriefingMerchandise from "@/components/modules/task-management/ExecutiveBriefingMerchandise";
import ExecutiveBriefingFashion from "@/components/modules/task-management/ExecutiveBriefingFashion";
import ExecutiveBriefingTextile from "@/components/modules/task-management/ExecutiveBriefingTextile";
import RawGoodsInventoryMain from "@/components/modules/RawGoodsInventoryMain";
import InventoryStock from "@/components/modules/inventory/InventoryStock";
import InventoryIssue from "@/components/modules/inventory/InventoryIssue";
import InventoryReceive from "@/components/modules/inventory/InventoryReceive";
import Showroom from "@/components/modules/Showroom";
import TechPack from "@/components/modules/TechPack";
import LnxReports from "@/components/modules/LnxReports";
import PreProductionCosting from "@/components/modules/PreProductionCostingNew";

function AppContent({ token, user, handleLogin, handleLogout }) {
  const navigate = useNavigate();
  const monitoringIntervalRef = useRef(null);

  // Setup axios interceptor for token expiration handling
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && error.response?.data?.detail === "Token expired") {
          // Clear storage and redirect to login
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          if (monitoringIntervalRef.current) {
            stopLocationMonitoring(monitoringIntervalRef.current);
            monitoringIntervalRef.current = null;
          }
          navigate("/login");
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [navigate]);

  // GPS Location Monitoring - Start when user is logged in
  useEffect(() => {
    if (token && user) {
      // Only monitor GPS for GPS-restricted users
      if (!user.gps_restricted) {
        console.log('✅ User not GPS-restricted - monitoring disabled');
        return;
      }

      console.log('🌍 GPS-restricted user - starting hourly location monitoring');

      // Handle location violation
      const handleLocationViolation = (result) => {
        console.error('🚨 GPS VIOLATION - Auto-logout triggered');
        
        // Stop monitoring
        if (monitoringIntervalRef.current) {
          stopLocationMonitoring(monitoringIntervalRef.current);
          monitoringIntervalRef.current = null;
        }

        // Show error toast
        toast.error('This database is inaccessible outside permitted zone.', { duration: 6000 });

        // Logout user
        handleLogout();
        
        // Redirect to login
        navigate('/login');
      };

      // Start monitoring
      const intervalId = startLocationMonitoring(user, handleLocationViolation);
      monitoringIntervalRef.current = intervalId;

      // Cleanup on unmount or logout
      return () => {
        if (monitoringIntervalRef.current) {
          stopLocationMonitoring(monitoringIntervalRef.current);
          monitoringIntervalRef.current = null;
        }
      };
    }
  }, [token, user, handleLogout, navigate]);

  return (
    <Routes>
      <Route
        path="/login"
        element={<Login onLogin={handleLogin} />}
      />
          <Route
            path="/dashboard"
            element={<Dashboard user={user} onLogout={handleLogout} />}
          />
          <Route
            path="/design-studio"
            element={
              <DesignHub user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/design-studio/fashion-design"
            element={
              <FashionDesign user={user} />
            }
          />
          <Route
            path="/task-management"
            element={
              <TaskManagement user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/task-management/sampling-time-tracker"
            element={
              <SamplingTimeTracker user={user} />
            }
          />
          <Route
            path="/task-management/design-sampling"
            element={
              <DesignSampling user={user} />
            }
          />
          <Route
            path="/task-management/merchandise"
            element={
              <Merchandise user={user} />
            }
          />
          <Route
            path="/task-management/digital-design"
            element={
              <DigitalDesignTask user={user} />
            }
          />
          <Route
            path="/task-management/meeting-minutes"
            element={
              <MeetingMinutes user={user} />
            }
          />
          <Route
            path="/task-management/weekly-progress"
            element={
              <WeeklyProgress user={user} />
            }
          />
          <Route
            path="/task-management/executive-briefing"
            element={
              <ExecutiveBriefing user={user} />
            }
          />
          <Route
            path="/task-management/executive-briefing/merchandise"
            element={
              <ExecutiveBriefingMerchandise user={user} />
            }
          />
          <Route
            path="/task-management/executive-briefing/fashion-design"
            element={
              <ExecutiveBriefingFashion user={user} />
            }
          />
          <Route
            path="/task-management/executive-briefing/textile-design"
            element={
              <ExecutiveBriefingTextile user={user} />
            }
          />
          <Route
            path="/design-studio/wastage"
            element={
              <Wastage user={user} />
            }
          />
          <Route
            path="/design-studio/gate-pass"
            element={
              <GatePass user={user} />
            }
          />


          <Route
            path="/work-orders"
            element={
              <WorkOrders user={user} />
            }
          />
          <Route
            path="/inventory"
            element={
              <RawGoodsInventoryMain user={user} />
            }
          />
          <Route
            path="/inventory/stock"
            element={
              <InventoryStock user={user} />
            }
          />
          <Route
            path="/inventory/issue"
            element={
              <InventoryIssue user={user} />
            }
          />
          <Route
            path="/inventory/receive"
            element={
              <InventoryReceive user={user} />
            }
          />
          <Route
            path="/personnel-management"
            element={
              <UserManagement user={user} />
            }
          />
          <Route
            path="/user-access-control"
            element={
              <UserAccessControl user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/employee-actions-ledger"
            element={
              <EmployeeActionsLedger user={user} />
            }
          />

          <Route
            path="/inventory/stock"
            element={
              <InventoryStock user={user} />
            }
          />
          <Route
            path="/inventory/receive"
            element={
              <InventoryReceive user={user} />
            }
          />
          <Route
            path="/inventory/issue"
            element={
              <InventoryIssue user={user} />
            }
          />
          <Route
            path="/master-data"
            element={
              <MasterData user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/master-data/vendors"
            element={
              <VendorData user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/master-data/fabrics"
            element={
              <FabricData user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/master-data/trims"
            element={
              <TrimsData user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/master-data/packaging"
            element={
              <PackagingData user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/master-data/articles"
            element={
              <ArticleData user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/master-data/digital-designs"
            element={
              <DigitalDesignData user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/inventory"
            element={
              <Inventory user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/incoming-orders"
            element={
              <IncomingOrders user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/purchase-orders"
            element={
              <PurchaseOrders user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/finished-goods-inventory"
            element={
              <Showroom user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/design-studio/tech-pack"
            element={
              <TechPack user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/design-studio/lnx-reports"
            element={
              <LnxReports user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/design-studio/pre-production-costing"
            element={
              <PreProductionCosting user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/sampling-dashboard"
            element={
              <SamplingDashboard user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/sample-tracker"
            element={
              <SampleTracker user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/work-orders"
            element={
              <WorkOrders user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/time-tracker"
            element={
              <TimeTracker user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/techpack"
            element={
              <Techpack user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/costing"
            element={
              <Costing user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/production-articles"
            element={
              <ProductionArticles user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/production-orders"
            element={
              <ProductionOrders user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/production-purchase-orders"
            element={
              <ProductionPurchaseOrders user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/production-raw-inventory"
            element={
              <ProductionRawInventory user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/production-finished-inventory"
            element={
              <ProductionFinishedInventory user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/production-tracking"
            element={
              <ProductionPlanning user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/quality-control"
            element={
              <QualityControl user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/cutting-room"
            element={
              <CuttingRoom user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/sewing-floor"
            element={
              <SewingFloor user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/finishing-packing"
            element={
              <FinishingPacking user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/production-reports"
            element={
              <ProductionReports user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/delivery-management"
            element={
              <DeliveryManagement user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/production-costing"
            element={
              <ProductionCosting user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/external-work-orders"
            element={
              <ExternalWorkOrders user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/production-tracker"
            element={
              <ProductionTracker user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/expenses"
            element={
              <Expenses user={user} onLogout={handleLogout} />
            }
          />
          {/* Personnel route removed */}
          {/* Root path redirects based on login status */}
          <Route 
            path="/" 
            element={
              token && user ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
    </Routes>
  );
}

function App() {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });

  useEffect(() => {
    if (token) {
      localStorage.setItem("token", token);
    } else {
      localStorage.removeItem("token");
    }
  }, [token]);

  const handleLogin = (newToken, userData) => {
    setToken(newToken);
    setUser(userData);
    localStorage.setItem("token", newToken);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("modules_access");
  };

  return (
    <div className="App">
      <BrowserRouter>
        <AppContent 
          token={token} 
          user={user} 
          handleLogin={handleLogin} 
          handleLogout={handleLogout} 
        />
      </BrowserRouter>
    </div>
  );
}

export default App;
